import styled, { createGlobalStyle, css } from "styled-components";

const colors = {
  primary: "#0052CC",
  accent: "#F8981D",
  background: "#f8f9fa",
  cardBackground: "#FFFFFF",
  textPrimary: "#172B4D",
  textSecondary: "#6B778C",
  border: "#DFE1E6",
  success: "#28a745",
  danger: "#dc3545",
  warning: "#F8981D",
};

const bp = {
  xl: 1400,
  lg: 1200,
  md: 992,
  sm: 768,
  xs: 576,
};

export const DevExtremeModalGlobalStyles = createGlobalStyle`
  .custom-form-popup .dx-popup-title .dx-toolbar-label {
    white-space: normal !important;
    line-height: 1.4 !important;
    height: auto !important;
  }

  .custom-form-popup .dx-popup-content {
    padding: 0 !important;
    overflow: auto !important;
    max-height: 90vh;
  }

  .custom-form-popup.dx-overlay-content {
    border-radius: 14px !important;
    overflow: hidden;
  }

  .rechazo-motivo-popup-wrapper {
    z-index: 16000 !important;
  }

  .rechazo-motivo-popup-wrapper .dx-overlay-shader {
    z-index: 15999 !important;
  }
`;

export const PageContainer = styled.div`
  background-color: ${colors.background};
  padding: 40px;
  min-height: 100%;

  @media (max-width: ${bp.sm}px) {
    padding: 24px 16px;
  }
`;

export const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

export const Title = styled.h1`
  font-size: clamp(20px, 2.2vw, 24px);
  line-height: 1.2;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin: 0;
`;

export const FilterBar = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(140px, 1fr));
  gap: 12px;
  align-items: end;
  background: ${colors.cardBackground};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  padding: 16px;
  margin: 12px 0 18px;

  @media (max-width: ${bp.xl}px) {
    grid-template-columns: repeat(3, minmax(160px, 1fr));
  }
  @media (max-width: ${bp.md}px) {
    grid-template-columns: repeat(2, minmax(160px, 1fr));
  }
  @media (max-width: ${bp.sm}px) {
    grid-template-columns: 1fr;
  }

  & > button {
    justify-self: start;
    height: 40px;
    @media (max-width: ${bp.md}px) {
      justify-self: stretch;
    }
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const Label = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: ${colors.textSecondary};
  letter-spacing: 0.2px;
`;

const baseInput = css`
  height: 40px;
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 0 12px;
  outline: none;
  font-size: 14px;
  color: ${colors.textPrimary};
  background: #fff;
  width: 100%;
  min-width: 0;

  &:focus {
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 82, 204, 0.12);
  }
`;

export const StyledSelect = styled.select`
  ${baseInput}
  appearance: none;
  background-image:
    linear-gradient(45deg, transparent 50%, ${colors.textSecondary} 50%),
    linear-gradient(135deg, ${colors.textSecondary} 50%, transparent 50%);
  background-position: calc(100% - 18px) 50%, calc(100% - 12px) 50%;
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
`;

export const StyledInput = styled.input`
  ${baseInput}
`;

export const DateInput = styled.input`
  ${baseInput}
`;

export const GridContainer = styled.div`
  background-color: ${colors.cardBackground};
  border-radius: 12px;
  border: 1px solid ${colors.border};
  padding: 8px;
  overflow: hidden;

  .dx-datagrid-headers {
    background: #f7f5f0;
    font-weight: 700;
  }
`;

export const EstadoCell = styled.div<{ $tone: "warning" | "success" | "danger" }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: ${({ $tone }) =>
    $tone === "success"
      ? colors.success
      : $tone === "danger"
        ? colors.danger
        : colors.warning};
`;

export const InfoButton = styled.button`
  border: none;
  background: #e9ecef;
  color: ${colors.textSecondary};
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #dee2e6;
    color: ${colors.textPrimary};
  }
`;

export const ActionButton = styled.button`
  background-color: ${colors.accent};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #e07e0f;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const MutedText = styled.span`
  color: ${colors.textSecondary};
  font-size: 13px;
`;

export const PopupBody = styled.div`
  padding: 8px 4px 0;
  color: ${colors.textPrimary};
  font-size: 14px;
  line-height: 1.5;
`;

export const PopupFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 16px;
`;

export const SecondaryButton = styled.button`
  background: #fff;
  color: ${colors.danger};
  border: 1px solid ${colors.danger};
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${colors.danger};
    color: #fff;
  }
`;

export const PrimaryOrangeButton = styled.button`
  background: ${colors.accent};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: #e07e0f;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
